import { FormTextField } from '@orbis-systems/orbis-react-toolkit';
import { ErrorMessage, FormikProps } from 'formik';
import React from 'react';

import { getCurrentUser } from '@app/redux/auth/auth.selectors';
import { useAppSelector } from '@app/redux/hooks';

import { ButtonBase, Grid, Typography } from '@mui/material';

import Form from '@components/forms';
import SubmitButton from '@components/submitButton';

import useVerifyPhoneForm from './hooks';
import { VerifyPhoneFormProps, VerifyPhoneFormValues } from './interfaces';
import { BackSubmitButton } from './styles';

function VerifyPhoneForm({ initialValues, onSubmit, onDecline }: VerifyPhoneFormProps) {
	const { isSendingVerificationPhoneCode, handleSendVerificationPhoneCode, formik } = useVerifyPhoneForm({
		initialValues,
		onSubmit,
	});
	const currentUser = useAppSelector(getCurrentUser);

	return (
		<VerifyPhoneContainer
			handleSendVerificationPhoneCode={handleSendVerificationPhoneCode}
			isSendingVerificationPhoneCode={isSendingVerificationPhoneCode}
			phone={currentUser?.phone}
			onDecline={onDecline}
			formik={formik}
		/>
	);
}

interface VerifyPhoneContainerProps {
	handleSendVerificationPhoneCode: () => void;
	isSendingVerificationPhoneCode: boolean;
	onDecline?: () => void;
	formik: FormikProps<VerifyPhoneFormValues>;
	phone?: string;
}

export const VerifyPhoneContainer = ({
	handleSendVerificationPhoneCode,
	isSendingVerificationPhoneCode,
	onDecline,
	formik,
	phone,
}: VerifyPhoneContainerProps) => {
	const isSubmitDisabled = formik.isSubmitting || isSendingVerificationPhoneCode;

	return (
		<Form formik={formik}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="subtitle1">
						<b>Verify phone number</b>
					</Typography>
				</Grid>

				{phone ? (
					<Grid item xs={12}>
						<Typography variant="body1" sx={{ fontSize: 16, fontWeight: 400 }}>
							Enter the verification code sent to <b>{phone}</b>
						</Typography>
					</Grid>
				) : null}

				<Grid item xs={12}>
					<FormTextField
						focused
						name={'code'}
						label={'Verification code'}
						autoComplete="off"
						disabled={isSubmitDisabled}
					/>
				</Grid>

				<Grid item xs={12} textAlign={'right'}>
					<ButtonBase onClick={handleSendVerificationPhoneCode} disabled={isSendingVerificationPhoneCode}>
						<Typography variant="body2">
							<b>Resend code?</b>
						</Typography>
					</ButtonBase>
				</Grid>

				<Grid
					item
					xs={12}
					sx={{
						color: theme => theme.palette.error.main,
						fontSize: 14,
					}}
				>
					<ErrorMessage name="resendVerificationPhoneCodeError" />
				</Grid>

				<Grid item xs={12}>
					<Grid container spacing={3}>
						{onDecline && (
							<Grid item xs={6}>
								<BackSubmitButton
									fullWidth
									type="button"
									variant="contained"
									color="secondary"
									onClick={onDecline}
									disabled={isSubmitDisabled}
								>
									Back
								</BackSubmitButton>
							</Grid>
						)}

						<Grid item xs={onDecline ? 6 : 12}>
							<SubmitButton label={'Verify'} disabled={isSubmitDisabled} />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Form>
	);
};

export default VerifyPhoneForm;
